import { isAfter, isBefore } from "date-fns";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useIntegrationReportingAggregated } from "../api/useReportingData";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { DateRangeQuickFilters } from "@/components/ui/v2/date-range-picker";
import { ROUTES } from "@/constants/routes";
import { useIntegrationModule } from "@/features/integrations/hooks/useIntegrationModule";
import { useIntegrations } from "@/features/integrations/hooks/useIntegrations";
import { useModuleConfigurationSchema } from "@/features/integrations/hooks/useModuleConfiguration";
import {
  getActionHandlers,
  renderComponent,
} from "@/features/integrations/shared/renderer";
import type { RenderContext } from "@/features/integrations/shared/renderer/types";
import { decodeSlug } from "@/features/integrations/utils/slug";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import useToast from "@/lib/hooks/useToast";
import { formatLocalDateOnly, parseLocalDateOnly } from "@/utils/dateUtils";

const today = new Date();
const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
const oneWeekAgo = new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000);
const reportingMinDate =
  parseLocalDateOnly("2026-01-01") ?? new Date(2026, 0, 1);

const parseDate = (s: string | null, fallback: Date) =>
  parseLocalDateOnly(s ?? undefined) ?? fallback;

function clampToReportingRange(d: Date, min: Date, max: Date): Date {
  const out = new Date(d);
  if (isBefore(out, min)) return new Date(min);
  if (isAfter(out, max)) return new Date(max);
  return out;
}

function ClientReportingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { integrationName, clientId } = useParams<{
    integrationName: string;
    clientId?: string;
  }>();
  const { tenantId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const decodedName = integrationName ? decodeSlug(integrationName) : null;
  const backendIntegrationName = decodedName ?? "";

  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler();

  const rawStartDate = parseDate(searchParams.get("startDate"), oneWeekAgo);
  const rawEndDate = parseDate(searchParams.get("endDate"), yesterday);
  const s = clampToReportingRange(rawStartDate, reportingMinDate, yesterday);
  const e = clampToReportingRange(rawEndDate, reportingMinDate, yesterday);
  const startDateClamped = s.getTime() <= e.getTime() ? s : e;
  const endDateClamped = s.getTime() <= e.getTime() ? e : s;

  const integrationsQuery = useIntegrations({
    tenantId,
    clientId: clientId ?? null,
  });
  const integration = integrationsQuery.data?.find(
    (i) => i.name.toLowerCase() === backendIntegrationName?.toLowerCase(),
  );
  const integrationBreadcrumbLabel = integration?.displayName
    ? t(integration.displayName)
    : (integration?.name ?? decodedName ?? "Integration");
  const translatedIntegrationName = integration?.displayName
    ? t(integration.displayName)
    : integration?.name || backendIntegrationName || "";

  const breadcrumbs = useBreadcrumbs({
    items: clientId
      ? [
        {
          label: t("common.integrations"),
          href: `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}`,
        },
        {
          label: integrationBreadcrumbLabel,
          href:
              integrationName && clientId
                ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}`
                : undefined,
        },
        { label: t("integrations.modules.names.reporting", "Reporting") },
      ]
      : [
        { label: t("common.integrations"), href: `/${ROUTES.integrations}` },
        {
          label: integrationBreadcrumbLabel,
          href: integrationName
            ? `/${ROUTES.integrations}/${integrationName}`
            : undefined,
        },
        { label: t("integrations.modules.names.reporting", "Reporting") },
      ],
  });

  const moduleQuery = useIntegrationModule({
    integrationName: backendIntegrationName,
    moduleName: "reporting",
    tenantId: tenantId ?? undefined,
    clientId: clientId ?? undefined,
  });

  const schemaQuery = useModuleConfigurationSchema(
    backendIntegrationName,
    "reporting",
    clientId
      ? {
        level: "client",
        tenantId: tenantId ?? null,
        clientId: clientId,
      }
      : {
        level: "tenant",
        tenantId: tenantId ?? null,
      },
    !!backendIntegrationName,
  );

  const reportingDataQuery = useIntegrationReportingAggregated(
    backendIntegrationName,
    {
      startDate: startDateClamped,
      endDate: endDateClamped,
      tenantId: tenantId ?? undefined,
      clientId,
      page,
      pageSize,
      sortColumn: orderBy ?? null,
      sortDirection:
        descending === undefined ? null : descending ? "desc" : "asc",
      searchValue: debouncedSearchValue,
    },
  );

  useEffect(() => {
    if (moduleQuery.isPending) return;
    const shouldRedirect = moduleQuery.isError || !moduleQuery.data?.isActive;
    if (shouldRedirect && integrationName) {
      const redirectPath = clientId
        ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}`
        : `/${ROUTES.integrations}/${integrationName}`;
      navigate(redirectPath, { replace: true });
    }
  }, [
    moduleQuery.isPending,
    moduleQuery.isError,
    moduleQuery.data,
    integrationName,
    clientId,
    navigate,
  ]);

  const onFilterChange = useCallback(
    (key: string, value: unknown) => {
      const newParams = new URLSearchParams(searchParams);
      if (key === "dateRange" && value != null && typeof value === "object") {
        const range = value as { startDate?: string; endDate?: string };
        if (range.startDate) newParams.set("startDate", range.startDate);
        else newParams.delete("startDate");
        if (range.endDate) newParams.set("endDate", range.endDate);
        else newParams.delete("endDate");
      } else {
        const str = value != null ? String(value) : "";
        if (key === "startDate") {
          if (str) newParams.set("startDate", str);
          else newParams.delete("startDate");
        } else if (key === "endDate") {
          if (str) newParams.set("endDate", str);
          else newParams.delete("endDate");
        }
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const startDateStr = formatLocalDateOnly(startDateClamped);
  const endDateStr = formatLocalDateOnly(endDateClamped);
  useEffect(() => {
    const rawStart = searchParams.get("startDate");
    const rawEnd = searchParams.get("endDate");
    if (rawStart !== startDateStr || rawEnd !== endDateStr) {
      const next = new URLSearchParams(searchParams);
      next.set("startDate", startDateStr);
      next.set("endDate", endDateStr);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, startDateStr, endDateStr, setSearchParams]);
  const filterValues = useMemo(
    () => ({
      startDate: startDateStr,
      endDate: endDateStr,
      dateRange: { startDate: startDateStr, endDate: endDateStr },
    }),
    [startDateStr, endDateStr],
  );

  const actionHandlers = useMemo(
    () => getActionHandlers(backendIntegrationName),
    [backendIntegrationName],
  );

  const executeAction = useCallback(
    async (
      actionKey: string,
      messages: { successMessage: string; errorMessage: string },
    ) => {
      const handler = actionHandlers[actionKey];
      if (!handler) return;
      try {
        await handler({
          integrationName: backendIntegrationName,
          moduleName: "reporting",
          tenantId: tenantId ?? undefined,
          clientId: clientId ?? undefined,
          onSuccess: () => {
            if (messages.successMessage) {
              showToast(t(messages.successMessage), "success");
            }
            reportingDataQuery.refetch();
          },
          onError: (error) => {
            showToast(
              messages.errorMessage ? t(messages.errorMessage) : error.message,
              "error",
            );
          },
        });
      } catch (error) {
        showToast(
          messages.errorMessage
            ? t(messages.errorMessage)
            : error instanceof Error
              ? error.message
              : String(error),
          "error",
        );
      }
    },
    [
      actionHandlers,
      backendIntegrationName,
      tenantId,
      clientId,
      showToast,
      t,
      reportingDataQuery,
    ],
  );

  const ui = useMemo(() => schemaQuery.data?.ui ?? [], [schemaQuery.data]);
  const tableData = useMemo(
    () => reportingDataQuery.data?.data ?? [],
    [reportingDataQuery.data],
  );
  const totalRecords = reportingDataQuery.data?.totalRecords ?? 0;

  const context = useMemo<RenderContext>(
    () => ({
      integrationName: backendIntegrationName,
      moduleName: "reporting",
      tenantId: tenantId ?? undefined,
      clientId: clientId ?? undefined,
      executeAction,
      tableData,
      isLoading: reportingDataQuery.isLoading || reportingDataQuery.isFetching,
      pagination: {
        page,
        pageSize,
        totalRecords,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
      },
      orderBy: orderBy ?? null,
      descending: descending ?? false,
      onSort: handleSort as RenderContext["onSort"],
      emptyMessage: t("reporting.noData", "No reporting data available"),
      searchValue: searchValue ?? "",
      onSearchChange: setSearchValue,
      filterValues,
      onFilterChange,
      dateRangePickerOptions: {
        minDate: reportingMinDate,
        maxDate: yesterday,
        allowedPresetIds: DateRangeQuickFilters.allExceptToday,
      },
    }),
    [
      backendIntegrationName,
      tenantId,
      clientId,
      executeAction,
      tableData,
      reportingDataQuery.isLoading,
      reportingDataQuery.isFetching,
      page,
      pageSize,
      totalRecords,
      setPage,
      setPageSize,
      orderBy,
      descending,
      handleSort,
      t,
      searchValue,
      setSearchValue,
      filterValues,
      onFilterChange,
    ],
  );

  const isModuleActive = moduleQuery.data?.isActive ?? false;

  if (!decodedName) {
    return (
      <ModernPageLayout breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">
            {t("reporting.integrationNotFound", "Integration not found")}
          </p>
        </div>
      </ModernPageLayout>
    );
  }

  if (moduleQuery.isPending || !isModuleActive) {
    return null;
  }

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      title={t("integrations.modules.names.reporting", "Reporting")}
      subtitle={t(
        "reporting.description",
        "View cost and performance data for {{integration}}",
        {
          integration: translatedIntegrationName,
        },
      )}
    >
      {ui.length > 0 ? (
        <div className="space-y-4">
          {ui.map((component, index) => (
            <div key={`${component.component}-${index}`} className="space-y-4">
              {renderComponent(component, context)}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-muted-foreground">
            {t(
              "reporting.noContentConfigured",
              "No content configured for this integration.",
            )}
          </p>
        </div>
      )}
    </ModernPageLayout>
  );
}

export default ClientReportingPage;
